import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '../api/client';
import type {
  CreatePromptVersionPayload,
  PromptDiffResponse,
  PromptInvocationDetail,
  PromptInvocationSummary,
  PromptVersionDetail,
  PromptVersionSummary,
} from '../types';

export type InvocationTargetKind = 'signal' | 'aggregator' | 'crm_enrichment';

export type PromptPurpose =
  | 'aggregator'
  // WP3/S3.3 — the SELL-SIDE aggregator prompt, composed by
  // `POST /api/admin/prompts/exit-aggregator/compose` and created INACTIVE. The Prompts tab is the
  // ONLY door through which it can go live, so a candidate that is not listed here is invisible and
  // the composer is undeliverable.
  | 'exit_aggregator'
  | 'classifier'
  | 'nda_review'
  // CRM enrichment lanes — mirrors _SUPPORTED_PURPOSES in backend/app/routers/prompts.py
  | 'crm_enrich_orgname'
  | 'crm_enrich_triage'
  | 'crm_enrich_deepdive'
  | 'crm_enrich_reconcile';

export interface CorrectionsVersionRow {
  prompt_version_id: number | null;
  purpose: string | null;
  version_tag: string | null;
  is_active: boolean;
  accepts: number;
  edit_accepts: number;
  rejects: number;
  correction_rate: number;
  reject_reasons: Record<string, number>;
  top_fields: Record<string, number>;
}

export interface CorrectionsSummaryResponse {
  days: number;
  sources: Record<string, number>;
  versions: CorrectionsVersionRow[];
}

/** E-15: per-prompt-version corrections readout (crm_enrichment_correction + crm_field_feedback). */
export function useCorrectionsSummary(days: number, enabled = true) {
  return useQuery<CorrectionsSummaryResponse>({
    queryKey: ['prompts', 'corrections-summary', days],
    queryFn: () => apiFetch<CorrectionsSummaryResponse>(`/api/admin/prompts/corrections-summary?days=${days}`),
    enabled,
    staleTime: 60_000,
  });
}

export function usePromptList(purpose: PromptPurpose | null) {
  const params = new URLSearchParams();
  if (purpose) params.set('purpose', purpose);
  const qs = params.toString();
  return useQuery<PromptVersionSummary[]>({
    queryKey: ['prompts', 'list', purpose ?? 'all'],
    queryFn: () => apiFetch<PromptVersionSummary[]>(
      `/api/admin/prompts/${qs ? `?${qs}` : ''}`,
    ),
  });
}

export function usePromptDetail(versionId: number | null) {
  return useQuery<PromptVersionDetail>({
    queryKey: ['prompts', 'detail', versionId],
    queryFn: () => apiFetch<PromptVersionDetail>(
      `/api/admin/prompts/${versionId}`,
    ),
    enabled: versionId !== null,
  });
}

export function usePromptDiff(aId: number | null, bId: number | null) {
  return useQuery<PromptDiffResponse>({
    queryKey: ['prompts', 'diff', aId, bId],
    queryFn: () => apiFetch<PromptDiffResponse>(
      `/api/admin/prompts/diff/${aId}/${bId}`,
    ),
    enabled: aId !== null && bId !== null && aId !== bId,
  });
}

export function useCreatePromptVersion() {
  const qc = useQueryClient();
  return useMutation<PromptVersionDetail, Error, CreatePromptVersionPayload>({
    mutationFn: (payload) => apiFetch<PromptVersionDetail>(
      '/api/admin/prompts/',
      { method: 'POST', body: JSON.stringify(payload) },
    ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['prompts'] });
    },
  });
}

export function useActivatePromptVersion() {
  const qc = useQueryClient();
  return useMutation<PromptVersionDetail, Error, number>({
    mutationFn: (versionId) => apiFetch<PromptVersionDetail>(
      `/api/admin/prompts/${versionId}/activate`,
      { method: 'POST', body: JSON.stringify({}) },
    ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['prompts'] });
    },
  });
}

export function usePromptInvocation(invocationId: number | null) {
  return useQuery<PromptInvocationDetail>({
    queryKey: ['prompts', 'invocation', invocationId],
    queryFn: () => apiFetch<PromptInvocationDetail>(
      `/api/admin/prompts/invocations/${invocationId}`,
    ),
    enabled: invocationId !== null,
  });
}

export function usePromptInvocationsForTarget(
  kind: InvocationTargetKind | null,
  targetId: number | null,
  enabled: boolean = true,
) {
  return useQuery<PromptInvocationSummary[]>({
    queryKey: ['prompts', 'invocations-by-target', kind, targetId],
    queryFn: () => apiFetch<PromptInvocationSummary[]>(
      `/api/admin/prompts/invocations/by-target/${kind}/${targetId}`,
    ),
    enabled: enabled && kind !== null && targetId !== null,
  });
}
